import {Box, Button, Grid, Paper, Typography} from "@mui/material";
import {Inventory2Outlined, LocalShippingOutlined, GroupsOutlined, PaymentsOutlined} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import useOverviewCount from "./use-overview-count";
import {AttentionItem, MetricCard} from "./overview-widgets";

const unavailable = {status: "unavailable"};
const MONTH_KEYS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function OverviewPanel({tenantId, onProducts, onOrders}) {
    const {t, i18n} = useTranslation();
    const products = useOverviewCount(tenantId, i18n.language);
    const pending = useOverviewCount(tenantId, i18n.language, "PENDING");
    const processing = useOverviewCount(tenantId, i18n.language, "PROCESSING");
    const shipped = useOverviewCount(tenantId, i18n.language, "SHIPPED");
    const orderCounts = [pending, processing, shipped];
    let activeStatus = ["error", "unavailable", "loading"].find(status => orderCounts.some(result => result.status === status)) || "success";
    const activeCount = orderCounts.reduce((sum, result) => sum + (result.count || 0), 0);
    if (activeStatus === "success" && !Number.isSafeInteger(activeCount)) activeStatus = "unavailable";
    const reloadOrders = () => Promise.all(orderCounts.map(result => result.reload()));
    const active = {status: activeStatus, count: activeCount,
        checkedAt: activeStatus === "success" ? new Date(Math.min(...orderCounts.map(result => result.checkedAt.getTime()))) : null,
        reload: reloadOrders};
    const now = new Date();
    // Browser locale data can fall back to English for Armenian; use our translated month names.
    const period = `${t(MONTH_KEYS[now.getMonth()])} ${now.getFullYear()}`;
    const metrics = [{title: "MyProducts", icon: Inventory2Outlined, result: products, description: t("OverviewProductsScope"),
        action: onProducts && {label: "OverviewManageProducts", onClick: onProducts}},
    {title: "ActiveOrders", icon: LocalShippingOutlined, result: active, description: t("OverviewActiveScope"),
        action: onOrders && {label: "WorkspaceViewOrders", onClick: () => onOrders()}},
    {title: "PartnerPharmacies", icon: GroupsOutlined, result: unavailable, description: t("OverviewPartnersUnavailable")},
    {title: "DistributorMonthlySalesAmount", icon: PaymentsOutlined, result: unavailable, description: t("OverviewSalesUnavailable", {period})}];
    return <>
        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2}}>
            <Typography variant="h6">{t("KeyMetrics")}</Typography>
            {tenantId && <Button onClick={() => {products.reload(); reloadOrders();}}>{t("OverviewRefresh")}</Button>}
        </Box>
        <Grid container spacing={2}>{metrics.map(metric => <Grid item xs={12} sm={6} lg={3} key={metric.title}>
            <MetricCard {...metric}/></Grid>)}</Grid>
        {tenantId && <Paper component="section" variant="outlined" sx={{mt: 3, p: {xs: 2, sm: 2.5}, borderRadius: 2}}>
            <Typography variant="h6" sx={{mb: 1}}>{t("OverviewNeedsAttention")}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>{t("OverviewAttentionScope")}</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}><AttentionItem title="OverviewPendingOrders" description="OverviewPendingDescription"
                    result={pending} onClick={() => onOrders?.("PENDING")}/></Grid>
                <Grid item xs={12} md={6}><AttentionItem title="OverviewProcessingOrders" description="OverviewProcessingDescription"
                    result={processing} onClick={() => onOrders?.("PROCESSING")}/></Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 2}}>{t("OverviewStockUnavailable")}</Typography>
        </Paper>}
    </>;
}

export default function TabOverview({tabValue, tenantId, onProducts, onOrders}) {
    const {i18n} = useTranslation();
    return <div role="tabpanel" hidden={tabValue !== 0} id="distributor-tabpanel-0" aria-labelledby="distributor-tab-0">
        {tabValue === 0 && <OverviewPanel key={`${tenantId}:${i18n.language}`} tenantId={tenantId} onProducts={onProducts} onOrders={onOrders}/>}
    </div>;
}
